<template>
    <page-content
        :loading="isLoading"
        :error="error"
    >
        <v-container grid-list-lg>
            <h1 class="display-1 mb-1">
                Product Returns
            </h1>
            <v-layout
                row
                align-center
            >
                <v-flex
                    key="text"
                >
                    <v-text-field
                        class="prp-append-outer-button"
                        v-model="filterTerm"
                        solo
                        clearable
                        clear-icon="fas fa-times"
                        hide-details
                        @click:clear="$nextTick(() => onChangeTerm())"
                        @keyup.enter="onChangeTerm"
                    >
                        <template v-slot:prepend-inner>
                            <v-icon
                                class="mr-1"
                                small
                            >
                                fas fa-search
                            </v-icon>
                        </template>
                        <template v-slot:append-outer>
                            <v-btn
                                color="primary"
                                @click="onChangeTerm"
                                :disabled="!filterTerm"
                            >
                                Search
                            </v-btn>
                        </template>
                    </v-text-field>
                </v-flex>
                <v-spacer />
                <v-flex shrink>
                    <v-icon
                        :color="filterStatus !== 'all' ? 'primary' : ''"
                    >
                        fas fa-clipboard-check
                    </v-icon>
                </v-flex>
                <!--filter by status -->
                <v-flex
                    key="status"
                    shrink
                >
                    <v-select
                        ref="statusFilter"
                        class="filter-input"
                        v-model="filterStatus"
                        :items="statusesArray"
                        label="Status"
                        single-line
                        solo
                        hide-details
                        @change="onChangeStatus"
                    />
                </v-flex>
                <!-- <v-spacer /> -->
            </v-layout>
            <v-layout
                row
                wrap
            >
                <v-flex xs12>
                    <v-card width="100%">
                        <v-data-table
                            must-sort
                            class="returns-table"
                            :headers="headers"
                            :items="returns"
                            :loading="tableLoading"
                            :pagination.sync="pagination"
                            :total-items="totalItems"
                            :rows-per-page-items="[5, 10, 25]"
                            no-data-text="No Product Returns Available"
                            @update:pagination="onPaginationUpdate"
                        >
                            <template v-slot:items="props">
                                <td>{{ statuses[props.item.status].text }}</td>
                                <td>
                                    <router-link
                                        :to="{name: 'readReturn', params: {id: props.item.uuid }}"
                                    >
                                        {{ props.item.uuid }}
                                    </router-link>
                                </td>
                                <td>{{ props.item.invoiceNumber }}</td>
                                <td>{{ props.item.company }}</td>
                                <td>{{ props.item.customerName }}</td>
                                <td>{{ props.item.proNumber }}</td>
                                <td>{{ props.item.crtNumber }}</td>
                                <td>{{ props.item.createdAt | formatDate('MM/DD/YYYY hh:mma') }}</td>
                            </template>
                        </v-data-table>
                    </v-card>
                </v-flex>
            </v-layout>
            <v-layout
                v-if="accessToSearchInvoice"
                justify-end
            >
                <v-flex shrink>
                    <v-btn
                        color="primary"
                        :to="{name: 'invoiceSearch'}"
                    >
                        Search Invoice
                    </v-btn>
                </v-flex>
            </v-layout>
        </v-container>
    </page-content>
</template>

<script>
import axios from 'axios'
import { statuses, statusesArray, PENDING_FREIGHT_GROUP, IN_TRANSIT } from '../utils/constants/statuses'
import { requestErrorMessage } from '../utils/constants/errors'
import UserBus from '../utils/buses/userBus'

const headers = [
    {
        text: 'Status',
        sortable: true,
        value: 'status'
    },
    {
        text: 'Return ID',
        sortable: true,
        value: 'uuid'
    },
    {
        text: 'Invoice Number',
        sortable: false,
        value: 'invoiceNumber'
    },
    {
        text: 'Company',
        sortable: false,
        value: 'company'
    },
    {
        text: 'Customer Name',
        sortable: false,
        value: 'customerName'
    },
    {
        text: 'PRO #',
        sortable: false,
        value: 'proNumber'
    },
    {
        text: 'CRT #',
        sortable: false,
        value: 'crtNumber'
    },
    {
        text: 'Start Date',
        sortable: true,
        value: 'createdAt'
    }
]

export default {
    data () {
        // determine default status when no query param is provided
        let defaultTerm = null
        let defaultStatus = 'all'

        // determine default sortBy when no query param is provided
        let defaultSortBy = 'createdAt'
        let defaultDescending = 'true'

        return {
            isLoading: true,
            tableLoading: false,
            error: null,
            returns: [],
            headers,
            statuses,
            filterTerm: this.$route.query.term ? this.$route.query.term : defaultTerm,
            filterStatus: this.$route.query.status ? this.$route.query.status : defaultStatus,
            pagination: {
                descending: this.$route.query.descending ? this.$route.query.descending === 'true' : defaultDescending,
                page: this.$route.query.page || 1,
                rowsPerPage: this.$route.query.rowsPerPage || 10,
                sortBy: this.$route.query.sortBy ? this.$route.query.sortBy : defaultSortBy,
                totalItems: 1
            },
            totalItems: 1
        }
    },
    computed: {
        statusIcon: function () {
            if (this.filterStatus === 'all') {
                return null
            }
            return statuses[this.filterStatus]
                ? statuses[this.filterStatus].icon
                : false
        },
        /**
         * filter statuses by user role
         */
        statusesArray: function () {
            let filtered = []
            if (UserBus.userIs.salesRep() || UserBus.userIs.customerService()) {
                filtered = [
                    {
                        text: 'All',
                        value: 'all'
                    },
                    ...statusesArray
                ]
            } else if (UserBus.userIs.freightGroup()) {
                filtered = [
                    {
                        text: 'All',
                        value: 'all'
                    },
                    statuses[PENDING_FREIGHT_GROUP],
                    statuses[IN_TRANSIT]
                ]
            }

            return filtered
        },
        accessToSearchInvoice: function () {
            return UserBus.userIs.salesRep()
        }
    },
    created () {
        this.isLoading = true

        this.getData()
            .finally(() => {
                this.isLoading = false
                this.tableLoading = false
            })
    },
    methods: {
        getData () {
            this.tableLoading = true

            // build sort param
            let sort = []
            if (this.pagination.sortBy) {
                sort.push(this.pagination.sortBy)
            }
            sort.push(this.pagination.descending ? 'desc' : 'asc')

            return axios.get('/api/returns/page', {
                params: {
                    sort: sort.join(','),
                    page: this.pagination.page - 1, // java expects pages starting at 0 index
                    size: this.pagination.rowsPerPage,
                    term: this.filterTerm,
                    status: this.filterStatus === 'all' ? null : this.filterStatus,
                    token: UserBus.token
                }
            })
                .then(res => {
                    const data = res.data.data
                    this.returns = data.content
                    this.totalItems = data.totalElements
                })
                .catch(err => {
                    console.log('Get returns Error', err)
                    this.error = requestErrorMessage
                })
                .finally(() => {
                    this.tableLoading = false
                })
        },
        onChangeTerm () {
            const term = this.filterTerm

            if (term) {
                this.$router.replace({
                    name: this.$route.name,
                    query: {
                        ...this.$route.query,
                        term
                    }
                })
            } else {
                let query = { ...this.$route.query }
                delete query.term

                this.$router.replace({
                    name: this.$route.name,
                    query: {
                        ...query
                    }
                })
            }

            this.getData()
        },
        onChangeStatus () {
            // replace route to include new status
            const status = this.filterStatus
            this.$router.replace({
                name: this.$route.name,
                query: {
                    ...this.$route.query,
                    status
                }
            })

            this.getData()
        },
        onPaginationUpdate (e) {
            const {
                descending,
                page,
                rowsPerPage,
                sortBy
            } = this.pagination

            this.$router.replace({
                name: this.$route.name,
                query: {
                    ...this.$route.query,
                    descending,
                    page,
                    rowsPerPage,
                    sortBy
                }
            })

            this.getData()
        }
    }
}
</script>

<style lang="scss" scoped>
.returns-table {
    /deep/ th i {
        margin-left: 4px;
    }
}
.filter-input {
    max-width: 270px;
}
</style>
